import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { NotificationsProvider } from "@/context/NotificationsContext";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import Login from "@/pages/auth/Login";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import Terms from "@/pages/Terms";
import ResetPassword from "@/pages/auth/ResetPassword";
import ClientHome from "@/pages/client/Home";
import ClientProjects from "@/pages/client/Projects";
import ProjectDetail from "@/pages/client/ProjectDetail";
import ClientDecisions from "@/pages/client/Decisions";
import ClientDocuments from "@/pages/client/Documents";
import ClientNotifications from "@/pages/client/ClientNotifications";
import ClientAccount from "@/pages/client/Account";
import Notifications from "@/pages/client/Notifications";
import { PortalProvider } from "@/portal/PortalContext";
import StaffDashboard from "@/pages/employee/Dashboard";
import Timesheet from "@/pages/employee/Timesheet";
import Assignments from "@/pages/employee/Assignments";
import Users from "@/pages/employee/Users";
import EmployeeAccount from "@/pages/employee/Account";
import Reports from "@/pages/employee/Reports";
import Approvals from "@/pages/employee/Approvals";
import TimeOff from "@/pages/employee/TimeOff";
import TimeOffApprovals from "@/pages/employee/TimeOffApprovals";
import TimeOffRecords from "@/pages/employee/TimeOffRecords";
import TimeOffAdmin from "@/pages/employee/TimeOffAdmin";
import Expenses from "@/pages/employee/Expenses";
import ExpensesApprovals from "@/pages/employee/ExpensesApprovals";
import PersonalDocs from "@/pages/employee/PersonalDocs";
import PersonalDocsAdmin from "@/pages/employee/PersonalDocsAdmin";
import AccessRequests from "@/pages/employee/AccessRequests";
import AccessApprovals from "@/pages/employee/AccessApprovals";
import Requests from "@/pages/employee/Requests";
import RequestsAdmin from "@/pages/employee/RequestsAdmin";
import Tasks from "@/pages/employee/Tasks";
import Planner from "@/pages/employee/Planner";
import AiAssistant from "@/pages/employee/AiAssistant";
import ClientConfig from "@/pages/employee/ClientConfig";
import OnboardingGate from "@/components/OnboardingGate";
import Budgets from "@/pages/employee/Budgets";
import ViewTimesheets from "@/pages/employee/ViewTimesheets";
import ViewLeaveCalendar from "@/pages/employee/ViewLeaveCalendar";
import ImportData from "@/pages/employee/ImportData";
import ReportsDocs from "@/pages/employee/ReportsDocs";
import AuditLog from "@/pages/employee/AuditLog";
import ProtectedRoute from "@/components/ProtectedRoute";

function LoginGate({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    if (user.role === "client") return <Navigate to="/portal" replace />;
    return <Navigate to="/staff" replace />;
  }
  return children;
}

function PermGate({ perm, anyOf, children }) {
  const { hasPermission } = useAuth();
  const ok = anyOf ? anyOf.some(p => hasPermission(p)) : hasPermission(perm);
  if (!ok) return <Navigate to="/staff" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <NotificationsProvider>
           <PortalProvider>
            <Routes>
              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="/landing" element={<Landing />} />
              <Route path="/login" element={<LoginGate><Login /></LoginGate>} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/termeni" element={<Terms />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              <Route path="/portal" element={<ProtectedRoute roles={["client"]}><ClientHome /></ProtectedRoute>} />
              <Route path="/portal/proiecte" element={<ProtectedRoute roles={["client"]}><ClientProjects /></ProtectedRoute>} />
              <Route path="/portal/proiecte/:id" element={<ProtectedRoute roles={["client"]}><ProjectDetail /></ProtectedRoute>} />
              <Route path="/portal/decizii" element={<ProtectedRoute roles={["client"]}><ClientDecisions /></ProtectedRoute>} />
              <Route path="/portal/documente" element={<ProtectedRoute roles={["client"]}><ClientDocuments /></ProtectedRoute>} />
              <Route path="/portal/notificari" element={<ProtectedRoute roles={["client"]}><ClientNotifications /></ProtectedRoute>} />
              <Route path="/portal/cont" element={<ProtectedRoute roles={["client"]}><ClientAccount /></ProtectedRoute>} />
              <Route path="/staff/notificari" element={<ProtectedRoute roles={["employee", "admin", "collaborator"]}><Notifications /></ProtectedRoute>} />

              <Route path="/staff" element={<ProtectedRoute roles={["employee", "admin", "collaborator"]}><StaffDashboard /></ProtectedRoute>} />
              <Route path="/staff/proiecte" element={<ProtectedRoute roles={["employee", "admin", "collaborator"]}><StaffDashboard /></ProtectedRoute>} />
              <Route path="/staff/pontaj" element={
                <ProtectedRoute roles={["employee", "admin", "collaborator"]}><PermGate perm="timesheet.self"><Timesheet /></PermGate></ProtectedRoute>
              } />
              <Route path="/staff/echipa" element={
                <ProtectedRoute roles={["employee", "admin", "collaborator"]}><PermGate perm="assignments.manage"><Assignments /></PermGate></ProtectedRoute>
              } />
              <Route path="/staff/utilizatori" element={
                <ProtectedRoute roles={["employee", "admin", "collaborator"]}><PermGate perm="users.manage"><Users /></PermGate></ProtectedRoute>
              } />
              <Route path="/staff/cont" element={
                <ProtectedRoute roles={["employee", "admin", "collaborator"]}><EmployeeAccount /></ProtectedRoute>
              } />
              <Route path="/staff/import" element={
                <ProtectedRoute roles={["employee", "admin", "collaborator"]}><PermGate perm="settings.manage"><ImportData /></PermGate></ProtectedRoute>
              } />
              <Route path="/staff/audit" element={
                <ProtectedRoute roles={["employee", "admin", "collaborator"]}><PermGate perm="users.manage"><AuditLog /></PermGate></ProtectedRoute>
              } />
              <Route path="/staff/rapoarte-proiect" element={
                <ProtectedRoute roles={["employee", "admin", "collaborator"]}><PermGate perm="projects.workspace"><ReportsDocs /></PermGate></ProtectedRoute>
              } />
              <Route path="/staff/ai" element={
                <ProtectedRoute roles={["employee", "admin", "collaborator"]}><PermGate perm="ai.assistant"><AiAssistant /></PermGate></ProtectedRoute>
              } />
              <Route path="/staff/client" element={
                <ProtectedRoute roles={["employee", "admin", "collaborator"]}><PermGate perm="client.manage"><ClientConfig /></PermGate></ProtectedRoute>
              } />
              <Route path="/staff/client/:pid" element={
                <ProtectedRoute roles={["employee", "admin", "collaborator"]}><PermGate perm="client.manage"><ClientConfig /></PermGate></ProtectedRoute>
              } />
              <Route path="/staff/rapoarte" element={
                <ProtectedRoute roles={["employee", "admin", "collaborator"]}><PermGate perm="reports.view"><Reports /></PermGate></ProtectedRoute>
              } />
              <Route path="/staff/aprobari" element={
                <ProtectedRoute roles={["employee", "admin", "collaborator"]}><PermGate perm="timesheet.approve"><Approvals /></PermGate></ProtectedRoute>
              } />

              <Route path="/staff/vizualizare-pontaje" element={
                <ProtectedRoute roles={["employee", "admin", "collaborator"]}><PermGate perm="timesheet.view_all"><ViewTimesheets /></PermGate></ProtectedRoute>
              } />
              <Route path="/staff/vizualizare-concedii" element={
                <ProtectedRoute roles={["employee", "admin", "collaborator"]}><PermGate perm="hr.manage"><ViewLeaveCalendar /></PermGate></ProtectedRoute>
              } />

              <Route path="/staff/timeoff" element={
                <ProtectedRoute roles={["employee", "admin", "collaborator"]}><TimeOff /></ProtectedRoute>
              } />
              <Route path="/staff/aprobari-timeoff" element={
                <ProtectedRoute roles={["employee", "admin", "collaborator"]}><TimeOffApprovals /></ProtectedRoute>
              } />
              <Route path="/staff/evidenta-timeoff" element={
                <ProtectedRoute roles={["employee", "admin", "collaborator"]}><PermGate anyOf={["timeoff.records", "timeoff.approve", "hr.manage"]}><TimeOffRecords /></PermGate></ProtectedRoute>
              } />
              <Route path="/staff/timeoff-admin" element={
                <ProtectedRoute roles={["employee", "admin", "collaborator"]}><PermGate perm="hr.manage"><TimeOffAdmin /></PermGate></ProtectedRoute>
              } />

              <Route path="/staff/decontari" element={
                <ProtectedRoute roles={["employee", "admin", "collaborator"]}><Expenses /></ProtectedRoute>
              } />
              <Route path="/staff/decontari-aprobari" element={
                <ProtectedRoute roles={["employee", "admin", "collaborator"]}><ExpensesApprovals /></ProtectedRoute>
              } />

              <Route path="/staff/documente" element={
                <ProtectedRoute roles={["employee", "admin", "collaborator"]}><PersonalDocs /></ProtectedRoute>
              } />
              <Route path="/staff/documente-admin" element={
                <ProtectedRoute roles={["employee", "admin", "collaborator"]}><PermGate perm="hr.manage"><PersonalDocsAdmin mode="employees" /></PermGate></ProtectedRoute>
              } />
              <Route path="/staff/documente-colaboratori" element={
                <ProtectedRoute roles={["employee", "admin", "collaborator"]}><PermGate perm="hr.manage"><PersonalDocsAdmin mode="collaborators" /></PermGate></ProtectedRoute>
              } />

              <Route path="/staff/proiecte-acces" element={
                <ProtectedRoute roles={["employee", "admin", "collaborator"]}><AccessRequests /></ProtectedRoute>
              } />              <Route path="/staff/cereri-acces-aprobari" element={
                <ProtectedRoute roles={["employee", "admin", "collaborator"]}><AccessApprovals /></ProtectedRoute>
              } />

              <Route path="/staff/cereri" element={
                <ProtectedRoute roles={["employee", "admin", "collaborator"]}><Requests /></ProtectedRoute>
              } />
              <Route path="/staff/cereri-admin" element={
                <ProtectedRoute roles={["employee", "admin", "collaborator"]}><PermGate perm="hr.manage"><RequestsAdmin /></PermGate></ProtectedRoute>
              } />

              <Route path="/staff/sarcini" element={
                <ProtectedRoute roles={["employee", "admin", "collaborator"]}><PermGate perm="projects.workspace"><Tasks /></PermGate></ProtectedRoute>
              } />
              <Route path="/staff/planner" element={
                <ProtectedRoute roles={["employee", "admin", "collaborator"]}><PermGate perm="projects.workspace"><Planner /></PermGate></ProtectedRoute>
              } />
              <Route path="/staff/bugete" element={
                <ProtectedRoute roles={["employee", "admin", "collaborator"]}><PermGate perm="projects.view"><PermGate perm="reports.view"><Budgets /></PermGate></PermGate></ProtectedRoute>
              } />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster position="top-right" richColors />
            <OnboardingGate />
           </PortalProvider>
          </NotificationsProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
